import { ArrowRight, BusFront, Check, Clock3, RefreshCw, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSchedules, useSchedulesActions } from './state';
import type { Arrival, RouteId, ScheduleDirection, ScheduleRoute, ScheduleStop } from './state/schedules.machine';

const findSelection = (
  routes: ScheduleRoute[],
  routeId: RouteId | null,
  directionId: string | null,
  stopId: string | null,
) => {
  const route = routes.find((item) => item.id === routeId);
  const direction = route?.directions.find((item) => item.id === directionId);
  const stop = direction?.stops.find((item) => item.id === stopId);
  return { route, direction, stop };
};

function RouteButton({ route, selected, onChoose }: { route: ScheduleRoute; selected: boolean; onChoose: () => void }) {
  return (
    <Button
      type="button"
      variant={selected ? 'default' : 'outline'}
      className="h-auto min-w-36 justify-start gap-3 px-4 py-3"
      aria-pressed={selected}
      data-jr-key={route.id === 'E' ? 'choose-route-e' : 'choose-route-44'}
      onClick={onChoose}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-primary-foreground/15 font-heading text-sm font-bold">
        {route.badge}
      </span>
      <span className="text-left">
        <span className="block text-sm font-semibold">{route.name}</span>
        <span className="block text-xs opacity-75">Scheduled service</span>
      </span>
    </Button>
  );
}

function ChoiceLabel({ children }: { children: React.ReactNode }) {
  return <p className="mb-2 text-sm font-medium text-muted-foreground">{children}</p>;
}

function ArrivalBoard({
  arrivals,
  status,
  stop,
  lastRefreshedAt,
  canRefresh,
  onRefresh,
}: {
  arrivals: Arrival[];
  status: 'idle' | 'ready' | 'missing';
  stop?: ScheduleStop;
  lastRefreshedAt: string | null;
  canRefresh: boolean;
  onRefresh: () => void;
}) {
  return (
    <section
      aria-labelledby="arrivals-heading"
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border px-5 py-5 sm:px-7">
        <div>
          <h2 id="arrivals-heading" className="font-heading text-xl font-semibold tracking-tight">
            {stop ? `Next scheduled buses · ${stop.name}` : 'Choose a stop to see departures'}
          </h2>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canRefresh}
          data-jr-key="refresh-arrivals"
          onClick={onRefresh}
        >
          <RefreshCw />
          Refresh schedule
        </Button>
      </div>

      {status === 'idle' && (
        <div className="flex min-h-40 items-center gap-3 px-5 py-8 text-muted-foreground sm:px-7">
          <BusFront className="size-5 shrink-0 text-primary" />
          <p>Choose a route, direction, and stop above. We’ll calculate the next scheduled trips for Seattle time.</p>
        </div>
      )}

      {status === 'missing' && (
        <div className="flex min-h-40 items-center gap-3 px-5 py-8 text-muted-foreground sm:px-7">
          <Clock3 className="size-5 shrink-0 text-amber-500" />
          <p>There are no source times remaining for this stop today. Check back after the service day rolls over.</p>
        </div>
      )}

      {status === 'ready' && (
        <div>
          <div className="divide-y divide-border">
            {arrivals.map((arrival, index) => (
              <div
                key={`${arrival.scheduledTime}-${arrival.serviceDay}`}
                className="grid grid-cols-[3.75rem_1fr_auto] items-center gap-4 px-5 py-4 sm:px-7"
              >
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="font-heading text-3xl font-semibold tracking-tight tabular-nums">{arrival.label}</span>
                <span
                  className={
                    index === 0 ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-sm text-muted-foreground'
                  }
                >
                  {arrival.minutesUntil === 0 ? 'Due now' : `${arrival.minutesUntil} min`}
                </span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border bg-muted/40 px-5 py-3 text-xs text-muted-foreground sm:px-7">
            <span>Scheduled for the {arrivals[0]?.serviceDay} service day.</span>
            <span aria-hidden="true">•</span>
            <span>Calculated for America/Los_Angeles, not real-time tracking.</span>
            {lastRefreshedAt && (
              <span className="tabular-nums">
                Updated {new Date(lastRefreshedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export function BusTimesPage() {
  const routes = useSchedules((snapshot) => snapshot.context.routes);
  const selectedRouteId = useSchedules((snapshot) => snapshot.context.selectedRouteId);
  const selectedDirectionId = useSchedules((snapshot) => snapshot.context.selectedDirectionId);
  const selectedStopId = useSchedules((snapshot) => snapshot.context.selectedStopId);
  const arrivals = useSchedules((snapshot) => snapshot.context.arrivals);
  const arrivalStatus = useSchedules((snapshot) => snapshot.context.arrivalStatus);
  const lastRefreshedAt = useSchedules((snapshot) => snapshot.context.lastRefreshedAt);
  const rememberedSelection = useSchedules((snapshot) => snapshot.context.rememberedSelection);
  const canRemember = useSchedules((snapshot) => snapshot.can({ type: 'schedule.remember-choice' }));
  const canRefresh = useSchedules((snapshot) => snapshot.can({ type: 'schedule.refresh-arrivals' }));
  const { chooseRoute, chooseDirection, chooseStop, rememberChoice, refreshArrivals } = useSchedulesActions();

  const { route, direction, stop } = findSelection(routes, selectedRouteId, selectedDirectionId, selectedStopId);
  const directions: ScheduleDirection[] = route?.directions ?? [];
  const stops: ScheduleStop[] = direction?.stops ?? [];

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex w-full max-w-[70ch] flex-col gap-7 px-5 py-8 sm:px-8 sm:py-12">
        <header className="border-b border-border pb-7">
          <div className="mb-5 flex items-center text-primary">
            <span className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground">
              <BusFront className="size-5" />
            </span>
          </div>
          <h1 className="font-heading text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">Seattle Bus Times</h1>
          <p className="mt-3 max-w-xl text-base leading-7 text-muted-foreground">
            Look up scheduled arrivals for the RapidRide E Line and Route 44. Times are calculated from the
            timetable—not live vehicle locations.
          </p>
        </header>

        <section aria-labelledby="route-heading" className="space-y-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 id="route-heading" className="font-heading text-lg font-semibold">
                Choose a route
              </h2>
            </div>
            <Badge variant="outline">Seattle</Badge>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {routes.map((item) => (
              <RouteButton
                key={item.id}
                route={item}
                selected={item.id === selectedRouteId}
                onChoose={() => chooseRoute(item.id)}
              />
            ))}
          </div>
        </section>

        {route && (
          <section aria-labelledby="direction-heading" className="space-y-3">
            <ChoiceLabel>Direction</ChoiceLabel>
            <h2 id="direction-heading" className="sr-only">
              Choose a direction
            </h2>
            <Select
              value={selectedDirectionId ?? ''}
              onValueChange={(value) => {
                if (typeof value === 'string' && value) chooseDirection(value);
              }}
            >
              <SelectTrigger data-jr-key="choose-direction" className="h-11 w-full bg-card text-left">
                <SelectValue placeholder="Select a direction" />
              </SelectTrigger>
              <SelectContent>
                {directions.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>
        )}

        {direction && (
          <section aria-labelledby="stop-heading" className="space-y-3">
            <ChoiceLabel>Stop</ChoiceLabel>
            <h2 id="stop-heading" className="sr-only">
              Choose a stop
            </h2>
            <Select
              value={selectedStopId ?? ''}
              onValueChange={(value) => {
                if (typeof value === 'string' && value) chooseStop(value);
              }}
            >
              <SelectTrigger data-jr-key="choose-stop" className="h-11 w-full bg-card text-left">
                <SelectValue placeholder="Select a stop" />
              </SelectTrigger>
              <SelectContent>
                {stops.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>
        )}

        {(route || direction || stop) && (
          <div
            className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
            aria-label="Current selection"
          >
            <span className="font-medium text-foreground">{route?.name}</span>
            <ArrowRight className="size-3" />
            {direction && (
              <>
                <span>{direction.label}</span>
                <ArrowRight className="size-3" />
              </>
            )}
            {stop && <span>{stop.name}</span>}
            {stop && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="ml-auto h-auto px-0"
                data-jr-key="change-stop"
                onClick={() => route && chooseRoute(route.id)}
              >
                <RotateCcw /> Change stop
              </Button>
            )}
          </div>
        )}

        <ArrivalBoard
          arrivals={arrivals}
          status={arrivalStatus}
          stop={stop}
          lastRefreshedAt={lastRefreshedAt}
          canRefresh={canRefresh}
          onRefresh={refreshArrivals}
        />

        {canRemember && (
          <Button
            type="button"
            variant={rememberedSelection ? 'secondary' : 'outline'}
            className="self-start"
            data-jr-key="remember-choice"
            onClick={rememberChoice}
          >
            <Check />
            {rememberedSelection ? 'Choice remembered' : 'Remember this choice'}
          </Button>
        )}
        <p className="text-center text-xs text-muted-foreground">
          Timetables can change. Confirm service details with King County Metro before making a time-sensitive trip.
        </p>
      </div>
    </main>
  );
}
